import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ExchangeMallProduct } from '@/mocks/home';

//卡片文本区估算高度（标题+代金券价格+原价值），用于两列分配
const TEXT_BLOCK_HEIGHT = 96;

//带图片宽高比的商品项（未加载前默认正方形）
interface MallItem extends ExchangeMallProduct {
  ratio: number; //高/宽
}

interface VoucherMallProps {
  products: ExchangeMallProduct[];
}

export default function VoucherMall({ products }: VoucherMallProps) {
  const navigate = useNavigate();
  //初始化时统一默认正方形比例，图片加载后按真实宽高比重排
  const [items, setItems] = useState<MallItem[]>(() =>
    products.map((p) => ({ ...p, ratio: 1 })),
  );

  /** 图片加载完成后记录真实宽高比并重排列 */
  const handleImageLoad = (id: number, ratio: number) => {
    setItems((prev) => prev.map((it) => (it.id === id && it.ratio !== ratio ? { ...it, ratio } : it)));
  };

  //瀑布流：每次放入当前较短的列
  const columns = useMemo(() => {
    const cols: MallItem[][] = [[], []];
    const heights = [0, 0];
    for (const item of items) {
      const h = item.ratio * 100 + TEXT_BLOCK_HEIGHT; //按列宽百分比折算
      const idx = heights[0] <= heights[1] ? 0 : 1;
      cols[idx].push(item);
      heights[idx] += h;
    }
    return cols;
  }, [items]);

  const renderCard = (item: MallItem) => (
    <div
      key={item.id}
      className="relative cursor-pointer active:scale-[0.98] transition-transform"
      onClick={() => navigate(`/exchange-detail/${item.id}`)}
    >
      <div className="relative rounded-xl overflow-hidden bg-background-100">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.currentTarget;
              if (naturalWidth > 0) handleImageLoad(item.id, naturalHeight / naturalWidth);
            }}
            className="w-full block object-cover"
          />
        ) : (
          <div className="w-full aspect-square flex items-center justify-center">
            <i className="ri-image-line text-foreground-300 text-2xl" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] rounded-full font-medium">
          兑换
        </span>
      </div>
      <div className="mt-2">
        <h4 className="text-foreground-900 text-sm font-medium line-clamp-1">
          {item.name}
        </h4>
        <div className="flex items-center gap-1 mt-1">
          <i className="ri-coins-line text-yellow-500 text-sm" />
          <span className="text-primary-500 text-sm font-bold">
            {Number(item.points).toLocaleString()}
          </span>
          <span className="text-foreground-400 text-[10px]">代金券</span>
        </div>
        <p className="text-foreground-300 text-xs mt-0.5">
          价值 ¥{Number(item.originalPrice).toLocaleString()}
        </p>
      </div>
    </div>
  );

  return (
    <div className="mx-3 mt-3 bg-white rounded-2xl p-3.5">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <i className="ri-gift-line text-white text-sm" />
          </div>
          <span className="text-foreground-900 font-semibold text-sm">代金券兑换商城</span>
        </div>
        <button
          className="flex items-center gap-0.5 text-foreground-400 text-xs cursor-pointer hover:text-foreground-600 transition-colors"
          onClick={() => navigate('/voucher-mall')}
        >
          更多
          <i className="ri-arrow-right-s-line text-sm" />
        </button>
      </div>

      {/* Products waterfall */}
      <div className="flex gap-3 items-start">
        {columns.map((col, idx) => (
          <div key={idx} className="flex-1 min-w-0 space-y-3">
            {col.map(renderCard)}
          </div>
        ))}
      </div>
    </div>
  );
}
